import { ViewMode } from '@sketchnote/gantt-chart';
import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import { TElement } from '@udecode/plate-core';
import { useKanbanStore } from 'src/features/kanban';
import { BoardItem, IntegratedApp } from 'src/features/kanban/component/Board/types';
import { KanbanState } from 'src/features/kanban/store/types';
import { useTableStore } from 'src/features/list/store';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { BoardType, useBoardService } from '../api/useBoardServices';

export type Filter = {
  filter: string;
  behaviour: 'is' | 'is_not' | 'is_set' | 'is_not_set';
  property?:
    | string
    | {
        label: string;
        id: string;
        color: string;
      };
};

export type BoardStoreState = {
  columns: KanbanState['columns'];
  labels: KanbanState['labels'];
  selectables: KanbanState['selectables'];
  totalTaskCount: number;
  creations: any;
  creationAssignee: any;
  activeFile: string;
  authorisation?: boolean;
  ganttView?: ViewMode;
  ganttTaskColorMode: 'status' | 'priority' | 'none';
  calendarCustomize: {
    isOpen: boolean;
    showAssignee: boolean;
    showCommentsIcon: boolean;
  };
  filters: Filter[];
  fields: any[];
  search: string;
  selectedItems: string[];
  setSelectedItems: (arg: string[]) => void;
  toggleSelectedItem: (arg: string) => void;
  clearSelectedItems: () => void;
  handleMoveSelectedItems: (fileId: string, selectedItems: string[]) => Promise<boolean>;
  handleDeleteSelectedItems: (selectedItems: string[]) => Promise<boolean>;
  onChangeSearch: (value: string) => void;
  onRemoveFilter: (index: number) => void;
  onResetFilter: () => void;
  onAddFilter: (value: Filter) => void;
  toggleCalendarCustomize: (isOpen?: boolean) => void;
  toggleShowCommentsIcon: (isOpen?: boolean) => void;
  toggleShowAssignee: (isOpen?: boolean) => void;
  onChangeFilter: (index: number, value: string) => void;
  onChangeFilterBehaviour: (
    index: number,
    value: 'is' | 'is_not' | 'is_set' | 'is_not_set'
  ) => void;
  onChangeFilterProperty: (index: number, value: any) => void;
  onAddQuickFilter: (filter: string, property: string) => void;
  onShowHideQuickFilter: (value: boolean) => void;
  showQuickFilter: boolean;
  isLoading: boolean;
  actions: {
    setLabels: (c: KanbanState['labels']) => void;
    setSelectables: (c: KanbanState['selectables']) => void;
    fetchBoard: (id?: string) => Promise<void>;
    fetchPublicBoardDetails: ({ id, boardId }: { id: string; boardId: string }) => Promise<void>;

    fetchLabel: ({
      id,
      isTaskTemplateId,
    }: {
      id: string;
      isTaskTemplateId?: boolean;
    }) => Promise<void>;
    editLabel: ({
      id,
      label,
      color,
      boardId,
      taskId,
    }: {
      id: string;
      label: string;
      color: string;
      boardId: string;
      taskId: string;
    }) => Promise<void>;
    deleteLabel: ({ boardId, labelId }: { boardId: string; labelId: string }) => Promise<void>;
    fetchSelectables: ({
      id,
      isTaskTemplateId,
    }: {
      id: string;
      isTaskTemplateId?: boolean;
    }) => Promise<void>;
    setItem: ({
      columnId,
      itemId,
      item,
    }: {
      columnId: string;
      itemId: string;
      item: BoardItem;
    }) => void;
    resetState: () => void;
    setIsPublicView: (value: boolean) => void;
    searchBoard: ({ id }: { id: string }) => Promise<void>;
    fetchTimer: () => Promise<void>;
    incrementTaskCount: () => void;
    setShowQuickFilter: (v: boolean) => void;
  };
  apps?: IntegratedApp[];
  timer?: {
    billable: boolean;
    isRunning: boolean;
    status: string;
    _id: string;
    task: string;
    label: any;
    tasktype: string;
    description: string;
    startDate: string;
    startTime: string;
    member: string;
    createdAt: string;
    updatedAt: string;
    boardTitle: string;
    taskTitle: string;
    fileId?: string;
    fileType?: string;
    board: string;
  };
} & BoardType;

const getInitialValue = () => ({
  columns: [],
  creations: [],
  labels: [],
  selectables: {},
  filters: [],
  search: '',
  createdAt: '',
  activeFile: '',
  id: '',
  name: '',
  totalTaskCount: 0,
  title: '',
  key: '',
  shared: [],
  creationAssignee: [],
  updatedAt: '',
  createdBy: '',
  color: '',
  default: false,
  fields: [],
  owner: { id: '', avatar: '', email: '', firstname: '', lastname: '', memberId: '' },
  status: '',
  updatedBy: { avatar: '', email: '', memberId: '', name: '' },
  sharedBy: '',
  access: '',
  icon: { id: '', type: '' },
  workspace: '',
  coverImage: '',
  showCover: false,
  isPublic: false,
  isPublicView: false,
  isLoading: false,
  authorisation: true,
  calendarCustomize: {
    isOpen: false,
    showAssignee: false,
    showCommentsIcon: false,
  },
  showQuickFilter: false,
});

export const useBoardStore = create<
  BoardStoreState,
  [['zustand/devtools', BoardStoreState], ['zustand/immer', BoardStoreState]]
>(
  devtools(
    zustandImmerMiddleware((set, get) => ({
      selectedItems: [],
      setSelectedItems: (arg) => {
        set((state) => {
          state.selectedItems = arg;
        });
      },
      toggleSelectedItem: (arg) => {
        set((state) => {
          const index = state.selectedItems.indexOf(arg);
          if (index > -1) {
            state.selectedItems.splice(index, 1);
          } else {
            state.selectedItems.push(arg);
          }
        });
      },
      clearSelectedItems: () => {
        set((state) => {
          state.selectedItems = [];
        });
      },
      handleMoveSelectedItems: async (fileId, items) => {
        const success = await useBoardService().moveTaskToAnotherBoard({
          destBoardId: fileId,
          taskIds: items,
        });
        if (!success) return false;
        set((state) => {
          const columns = JSON.parse(JSON.stringify(state.columns));
          columns.forEach((column) => {
            column.items = column.items.filter((item) => !items.includes(item.id));
          });
          state.columns = columns;
          state.selectedItems = [];
        });
        useKanbanStore.getState().removeItemsFromBoard(items);
        useTableStore.getState().removeItemsFromBoard(items);
        return true;
      },

      handleDeleteSelectedItems: async (items) => {
        const success = await useBoardService().deleteTasks({
          taskIds: items,
        });
        if (!success) return false;
        set((state) => {
          const columns = JSON.parse(JSON.stringify(state.columns));
          columns.forEach((column) => {
            column.items = column.items.filter((item) => !items.includes(item.id));
          });
          state.columns = columns;
          state.selectedItems = [];
        });
        useKanbanStore.getState().removeItemsFromBoard(items);
        useTableStore.getState().removeItemsFromBoard(items);
        return true;
      },

      toggleCalendarCustomize: (isOpen) => {
        set((state) => {
          state.calendarCustomize.isOpen =
            isOpen !== undefined ? isOpen : !state.calendarCustomize.isOpen;
        });
      },
      toggleShowCommentsIcon: (isOpen) => {
        set((state) => {
          state.calendarCustomize.showCommentsIcon =
            isOpen !== undefined ? isOpen : !state.calendarCustomize.showCommentsIcon;
        });
      },
      toggleShowAssignee: (isOpen) => {
        set((state) => {
          state.calendarCustomize.showAssignee =
            isOpen !== undefined ? isOpen : !state.calendarCustomize.showAssignee;
        });
      },
      onChangeSearch: (value: string) => {
        set((state) => {
          state.search = value;
        });
      },
      onRemoveFilter: (index: number) => {
        set((state) => {
          const filters = state.filters;
          filters.splice(index, 1);
          state.filters = filters;
        });
        get().actions.fetchBoard();
      },
      onResetFilter: () => {
        set((state) => {
          state.filters = [];
        });
        get().actions.fetchBoard();
      },
      onAddFilter: (value: Filter) => {
        set((state) => {
          const filters = state.filters;

          filters.push(value);
          state.filters = filters;
        });
        get().actions.fetchBoard();
      },

      onChangeFilter: (index: number, value: string) => {
        set((state) => {
          const filters = state.filters;
          filters[index].filter = value;
          filters[index].property = undefined;
        });
        get().actions.fetchBoard();
      },
      onChangeFilterBehaviour: (index, value) => {
        set((state) => {
          const filters = state.filters;
          filters[index].behaviour = value;
          if ((value === 'is_set' || value === 'is_not_set') && filters[index].property) {
            delete filters[index].property;
          }
        });

        get().actions.fetchBoard();
      },

      onChangeFilterProperty: (index: number, value: any) => {
        set((state) => {
          const filters = state.filters;
          filters[index].property = value;
        });
        get().actions.fetchBoard();
      },

      onAddQuickFilter: (filter: string, property: any) => {
        set((state) => {
          state.filters = [];
          const filters = state.filters;
          filters.push({
            filter: filter,
            behaviour: 'is',
            property: property,
          });
        });
        get().actions.fetchBoard();
      },

      onShowHideQuickFilter: async (value: boolean) => {
        set((state) => {
          state.showQuickFilter = value;
        });
        const response = await useBoardService().setShowQuickFilter(value);
        if (response) {
          set((s) => {
            s.showQuickFilter = value;
          });
        }
      },

      actions: {
        setLabels: (c) => {
          set((s) => {
            s.labels = c;
          });
        },

        setSelectables: (c) => {
          set((s) => {
            s.selectables = c;
          });
        },

        fetchBoard: async (id) => {
          const state = get();
          const filtered: any = state.filters;

          const board = await useBoardService().fetchBoardDetails({
            id: id || state.id,
            isPublicView: state.isPublicView,
            filters: filtered,
          });

          if (board && board !== 'Unauthorised access') {
            if (board.columns.length > 0) {
              const count = board.columns.reduce((a, column) => a + column?.items?.length, 0);

              set((s) => {
                s.totalTaskCount = count;
              });
            }
            set((s) => {
              return { ...s, ...board };
            });
          } else if (board === 'Unauthorised access') {
            set((s) => {
              s.authorisation = false;
            });
          }
        },

        fetchPublicBoardDetails: async ({ id, boardId }) => {
          const state = get();
          const filtered: any = state.filters;

          const board = await useBoardService().fetchPublicBoardDetails({
            id: id || state.id,
            boardId: boardId,
            filters: filtered,
          });

          if (board && board !== 'Unauthorised access') {
            set((s) => {
              return { ...s, ...board };
            });
          } else if (board === 'Unauthorised access') {
            set((s) => {
              s.authorisation = false;
            });
          }
        },
        searchBoard: async ({ id }) => {
          const state = get();
          const search = state.search;
          const board = await useBoardService().searchBoard({
            boardId: id,
            search: search,
          });
          if (board) {
            set((s) => {
              return { ...s, ...board };
            });
          }
        },

        /** fetch custom labels for task template, added isTaskTemplateId variable to let the BE know that
        this is task template call so data can be picked up from the right place.**/
        fetchLabel: async ({ id, isTaskTemplateId }) => {
          const response = await useBoardService().useLabelsForBoards({ id, isTaskTemplateId });
          const data = response?.data?.data;
          if (data) {
            const labelData = Object.keys(data).map((item) => ({
              id: item,
              label: data[item].label,
              value: data[item].label,
              color: data[item].color,
            }));

            set((s) => {
              s.labels = labelData;
            });
          }
        },
        /** edit custom labels for task */
        editLabel: async ({ id, label, color, boardId, taskId }) => {
          set((s) => {
            s.labels = s.labels.map((item) => {
              if (item.id === id) {
                return { ...item, label, color };
              }
              return item;
            });
          });
          await useBoardService().editLabel({ id, label, color, board: boardId, task: taskId });
        },
        deleteLabel: async ({ labelId, boardId }) => {
          set((s) => {
            s.labels = s.labels.filter((item) => item.id !== labelId);
          });
          await useBoardService().deleteLabel({ boardId, labelId });
        },
        /** fetch custom labels for task template, added isTaskTemplateId variable to let the BE know that
        this is task template call so data can be picked up from the right place.**/
        fetchSelectables: async ({ id, isTaskTemplateId }) => {
          const response = await useBoardService().useSelectablesById({ id, isTaskTemplateId });
          const data = response?.data?.data;
          if (data) {
            set((s) => {
              s.selectables = data;
            });
          }
        },

        setItem: async ({ columnId, itemId, item }) => {
          set((s) => {
            const colIndex = s.columns.findIndex((obj) => obj.id === columnId);
            const itemIndex = s.columns[colIndex]?.items?.findIndex((obj) => obj.id === itemId);
            if (colIndex > -1 && itemIndex > -1) {
              s.columns[colIndex].items[itemIndex] = item;
            }
          });
        },
        setIsPublicView: (value = true) => {
          set((s) => {
            s.isPublicView = value;
          });
        },
        resetState: () => {
          set((s) => {
            return { ...s, ...getInitialValue() };
          });
        },
        fetchTimer: async () => {
          const response = await useBoardService().fetchTimer();
          if (response)
            set((s) => {
              s.timer = {
                ...response,
                boardTitle: response.board_title,
                taskTitle: response.task_title,
              };
            });
        },
        setShowQuickFilter: async (value: boolean) => {
          set((s) => {
            s.showQuickFilter = value;
          });
        },

        incrementTaskCount: () => {
          set((s) => {
            s.totalTaskCount += 1;
          });
        },
      },
      ganttView: ViewMode.Day,
      ganttTaskColorMode: 'status',
      ...getInitialValue(),
    }))
  )
);
